import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftRight, Calendar, Check, Crop, Download,
  FlipHorizontal2, FlipVertical2, LayoutGrid, Layers,
  Palette, Redo2, RotateCcw, RotateCw, Save,
  Aperture, Scissors, Sliders, Sparkles, Sun, Type, Undo2, Video, X,
} from "lucide-react";
import { DEFAULT_ADJUSTMENTS, renderEditedBlob } from "../lib/imageEditor";
import { exportPdfFromImage, exportPhotoBlob, shareBlob } from "../lib/exportUtils";
import { putRecord, savePhoto } from "../lib/indexedDb";
import { loadBgModel, removeBg, compositeBg, BG_PRESETS } from "../lib/bgRemover";
import { loadDepthModel, getDepthMap, applyDepthBokeh } from "../lib/depthBokeh";
import VideoEditor from "./VideoEditor";

// ── tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { id: "light",     label: "Light",     Icon: Sun },
  { id: "color",     label: "Color",     Icon: Palette },
  { id: "detail",    label: "Detail",    Icon: Sliders },
  { id: "effects",   label: "Effects",   Icon: Sparkles },
  { id: "transform", label: "Transform", Icon: Crop },
  { id: "overlay",   label: "Overlay",   Icon: Type },
  { id: "presets",   label: "Presets",   Icon: Layers },
];

// ── slider groups ────────────────────────────────────────────────────────────

const LIGHT_SLIDERS = [
  { key: "exposure",   label: "Exposure",   min: -100, max: 100 },
  { key: "brightness", label: "Brightness", min: -60,  max: 60 },
  { key: "contrast",   label: "Contrast",   min: -60,  max: 60 },
  { key: "highlights", label: "Highlights", min: -60,  max: 60 },
  { key: "shadows",    label: "Shadows",    min: -60,  max: 60 },
  { key: "whites",     label: "Whites",     min: -60,  max: 60 },
  { key: "blacks",     label: "Blacks",     min: -60,  max: 60 },
];

const COLOR_SLIDERS = [
  { key: "temperature", label: "Temperature", min: -60, max: 60 },
  { key: "tint",        label: "Tint",        min: -60, max: 60 },
  { key: "vibrance",    label: "Vibrance",    min: -60, max: 60 },
  { key: "saturation",  label: "Saturation",  min: -60, max: 60 },
];

const DETAIL_SLIDERS = [
  { key: "sharpness",      label: "Sharpness",   min: 0,  max: 60 },
  { key: "clarity",        label: "Clarity",     min: -60, max: 60 },
  { key: "dehaze",         label: "Dehaze",      min: -60, max: 60 },
  { key: "noiseReduction", label: "Noise Reduc.", min: 0, max: 60 },
  { key: "grain",          label: "Grain",       min: 0,  max: 60 },
];

const EFFECT_SLIDERS = [
  { key: "vignette", label: "Vignette", min: -60, max: 60 },
  { key: "fade",     label: "Fade",     min: 0,   max: 60 },
  { key: "blur",     label: "Blur",     min: 0,   max: 12 },
  { key: "texture",  label: "Texture",  min: 0,   max: 60 },
];

// ── HSL ──────────────────────────────────────────────────────────────────────

const HSL_HUES = [
  { key: "red",     label: "Red",     color: "#ff5555" },
  { key: "orange",  label: "Orange",  color: "#ff9933" },
  { key: "yellow",  label: "Yellow",  color: "#ffdd22" },
  { key: "green",   label: "Green",   color: "#44cc55" },
  { key: "aqua",    label: "Aqua",    color: "#00cccc" },
  { key: "blue",    label: "Blue",    color: "#5599ff" },
  { key: "purple",  label: "Purple",  color: "#aa55ff" },
  { key: "magenta", label: "Magenta", color: "#ff55aa" },
];
const DEFAULT_HSL = Object.fromEntries(HSL_HUES.map((h) => [h.key, { hue: 0, sat: 0, lum: 0 }]));

// ── presets ──────────────────────────────────────────────────────────────────

const PRESET_CATS = ["All", "Portrait", "Landscape", "Film", "Street", "Artistic"];

const PRESETS = [
  // Portrait
  { id: "natural",     label: "Natural",     cat: "Portrait",  adj: { brightness: 6,  contrast: 8,   saturation: 10,  temperature: 12 } },
  { id: "soft-glow",   label: "Soft Glow",   cat: "Portrait",  adj: { brightness: 14, contrast: -10, highlights: -20, saturation: 6 } },
  { id: "editorial",   label: "Editorial",   cat: "Portrait",  adj: { contrast: 24,   shadows: -18,  highlights: -16, saturation: -8, vignette: 14 } },
  { id: "warm-skin",   label: "Warm Skin",   cat: "Portrait",  adj: { temperature: 30, tint: 8, brightness: 5, saturation: 10 } },
  // Landscape
  { id: "epic",        label: "Epic Vista",  cat: "Landscape", adj: { contrast: 26,   saturation: 30, shadows: -18, vignette: 22 } },
  { id: "golden",      label: "Golden Hr",   cat: "Landscape", adj: { temperature: 42, brightness: 8, contrast: 14, saturation: 22 } },
  { id: "blue-hr",     label: "Blue Hour",   cat: "Landscape", adj: { temperature: -30, tint: -12, contrast: 20, saturation: 24 } },
  { id: "moody",       label: "Moody",       cat: "Landscape", adj: { contrast: 30,   shadows: -32,  highlights: -22, saturation: 14, vignette: 32 } },
  // Film
  { id: "portra",      label: "Portra",      cat: "Film", adj: { temperature: 14, saturation: -8,  contrast: -5,  brightness: 6,  grain: 18, fade: 12 } },
  { id: "fuji-pro",    label: "Fuji Pro",    cat: "Film", adj: { temperature: -6, saturation: 12,  contrast: 10,  grain: 14 } },
  { id: "kodachrome",  label: "Kodachrome",  cat: "Film", adj: { temperature: 20, saturation: 24,  contrast: 16,  grain: 10 } },
  { id: "hp5",         label: "HP5 B&W",     cat: "Film", adj: { saturation: -100, contrast: 26, grain: 30, brightness: 4 } },
  { id: "cinestill",   label: "CineStill",   cat: "Film", adj: { temperature: 18, saturation: 14, contrast: -8, grain: 24, vignette: 20, fade: 10 } },
  // Street
  { id: "noir",        label: "Film Noir",   cat: "Street", adj: { saturation: -100, contrast: 34, brightness: -8, vignette: 30 } },
  { id: "tokyo",       label: "Tokyo",       cat: "Street", adj: { temperature: -16, saturation: 18, contrast: 20, brightness: -6 } },
  { id: "gritty",      label: "Gritty",      cat: "Street", adj: { contrast: 32,   saturation: -22, grain: 26, vignette: 24, brightness: -10 } },
  { id: "vintage-doc", label: "Vintage Doc", cat: "Street", adj: { temperature: 22, saturation: -16, contrast: -12, fade: 24, grain: 20 } },
  // Artistic
  { id: "teal-org",    label: "Teal/Orange", cat: "Artistic", adj: { temperature: 24, tint: -14, saturation: 26, contrast: 18 } },
  { id: "matte",       label: "Matte",       cat: "Artistic", adj: { contrast: -20, saturation: -14, brightness: 8, fade: 30 } },
  { id: "vivid",       label: "Vivid",       cat: "Artistic", adj: { saturation: 32, contrast: 20,  brightness: 8 } },
  { id: "drama",       label: "Drama",       cat: "Artistic", adj: { contrast: 34,   shadows: -26,  highlights: -18, vignette: 24, saturation: -10 } },
  { id: "pop",         label: "Pop Art",     cat: "Artistic", adj: { saturation: 42, contrast: 24,  brightness: 10 } },
  { id: "pastel",      label: "Pastel",      cat: "Artistic", adj: { brightness: 18, contrast: -14, saturation: 14, fade: 18 } },
];

// ── crop ratios ───────────────────────────────────────────────────────────────

const CROP_RATIOS = [
  { id: "free", label: "Free" },
  { id: "1:1",  label: "1:1" },
  { id: "4:3",  label: "4:3" },
  { id: "16:9", label: "16:9" },
  { id: "9:16", label: "9:16" },
  { id: "3:2",  label: "3:2" },
  { id: "4:5",  label: "4:5" },
  { id: "5:4",  label: "5:4" },
];

// ── frames ────────────────────────────────────────────────────────────────────

const FRAMES = [
  { id: "none",     label: "None",     color: "transparent" },
  { id: "white",    label: "White",    color: "#fff" },
  { id: "black",    label: "Black",    color: "#111" },
  { id: "film",     label: "Film",     color: "#222" },
  { id: "polaroid", label: "Polaroid", color: "#f5f3ee" },
  { id: "shadow",   label: "Shadow",   color: "rgba(0,0,0,.4)" },
];

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtVal(key, v) {
  if (key === "blur") return `${v}px`;
  if (key === "exposure") return v > 0 ? `+${(v / 33.3).toFixed(1)}EV` : v < 0 ? `${(v / 33.3).toFixed(1)}EV` : "0";
  return v > 0 ? `+${v}` : String(v);
}

function buildFilter(adj) {
  const bright = 100 + (adj.brightness || 0) + (adj.exposure || 0) * 0.35
    + (adj.whites || 0) * 0.45 - Math.abs(adj.blacks || 0) * 0.28;
  const cont   = 100 + (adj.contrast || 0)
    + (adj.clarity || 0) * 0.38
    + (adj.dehaze  || 0) * 0.24;
  const sat    = 100 + (adj.saturation || 0)
    + (adj.vibrance || 0) * 0.55
    + (adj.dehaze   || 0) * 0.18;
  const hue    = (adj.tint || 0) * 0.35 + (adj.temperature || 0) * 0.22;
  const blurPx = Math.max(0, (adj.blur || 0) - (adj.noiseReduction || 0) * 0.06);
  const sepia  = Math.min(0.85, Math.max(0, (adj.fade || 0) * 0.013));

  const parts = [
    `brightness(${Math.max(10, bright).toFixed(1)}%)`,
    `contrast(${Math.max(10, cont).toFixed(1)}%)`,
    `saturate(${Math.max(0, sat).toFixed(1)}%)`,
    `hue-rotate(${hue.toFixed(1)}deg)`,
  ];
  if (blurPx > 0.05) parts.push(`blur(${blurPx.toFixed(2)}px)`);
  if (sepia   > 0.01) parts.push(`sepia(${sepia.toFixed(3)})`);
  return parts.join(" ");
}

function presetFilter(adj) {
  return buildFilter({ ...DEFAULT_ADJUSTMENTS, ...adj });
}

// ── sub-components ────────────────────────────────────────────────────────────

function SliderItem({ sliderKey, label, min, max, value, onChange, onReset }) {
  const filled = max > 0 ? ((value - min) / (max - min)) * 100 : 50 + (value / Math.abs(min)) * 50;
  const isZero = value === 0;
  return (
    <div className="st-slider">
      <div className="st-slider-head">
        <span className="st-slider-label">{label}</span>
        <span className={`st-slider-val ${isZero ? "" : "nonzero"}`}>{fmtVal(sliderKey, value)}</span>
        {!isZero && (
          <button type="button" className="st-slider-reset" onClick={onReset} title="Reset to 0">
            <X size={9} />
          </button>
        )}
      </div>
      <input
        type="range"
        min={min} max={max}
        value={value}
        className="st-range"
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function SliderGroup({ sliders, adjustments, onChange, onReset }) {
  return (
    <div className="st-slider-group">
      {sliders.map((s) => (
        <SliderItem
          key={s.key}
          sliderKey={s.key}
          label={s.label}
          min={s.min}
          max={s.max}
          value={adjustments[s.key] || 0}
          onChange={(v) => onChange(s.key, v)}
          onReset={() => onReset(s.key)}
        />
      ))}
    </div>
  );
}

function PresetCard({ preset, url, isActive, onClick }) {
  const filterStr = presetFilter(preset.adj);
  return (
    <button
      type="button"
      className={`st-preset-card ${isActive ? "is-active" : ""}`}
      onClick={onClick}
      title={preset.label}
    >
      <div className="st-preset-thumb">
        {url
          ? <img src={url} alt="" className="st-preset-img" style={{ filter: filterStr }} />
          : <div className="st-preset-swatch" style={{ filter: filterStr }} />
        }
        {isActive && <div className="st-preset-check"><Check size={11} /></div>}
      </div>
      <span className="st-preset-label">{preset.label}</span>
    </button>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function PhotoEditor({ photo, onSaved }) {
  const sourceBlob = photo?.editedBlob || photo?.blob;
  const [url, setUrl] = useState("");

  // Editor state
  const [tab, setTab]               = useState("light");
  const [adjustments, setAdj]       = useState(DEFAULT_ADJUSTMENTS);
  const [hsl, setHsl]               = useState(DEFAULT_HSL);
  const [activeHue, setActiveHue]   = useState("red");
  const [colorMode, setColorMode]   = useState("basic"); // "basic" | "hsl"
  const [history, setHistory]       = useState([]);
  const [future, setFuture]         = useState([]);

  // Transform
  const [rotation,  setRotation]   = useState(0);
  const [straighten, setStraighten] = useState(0);
  const [flipX, setFlipX]           = useState(false);
  const [flipY, setFlipY]           = useState(false);
  const [cropRatio, setCropRatio]   = useState("free");
  const [frame, setFrame]           = useState("none");

  // Overlay
  const [label, setLabel]           = useState("");
  const [labelSize, setLabelSize]   = useState(2);
  const [labelPos, setLabelPos]     = useState("bottom");
  const [showDate, setShowDate]     = useState(false);

  // UI
  const [saving, setSaving]         = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [showBefore, setShowBefore] = useState(false);
  const [activePreset, setPreset]   = useState(null);
  const [presetCat, setPresetCat]   = useState("All");
  const [exportFmt, setExportFmt]   = useState("jpg");
  const [panelOpen, setPanelOpen]   = useState(true);

  // Background removal
  const [bgPhase, setBgPhase]             = useState("idle"); // idle | loading | processing | ready
  const [bgProgress, setBgProgress]       = useState(0);
  const [bgFg, setBgFg]                   = useState(null);
  const [bgChoice, setBgChoice]           = useState("transparent");
  const [bgCustomColor, setBgCustomColor] = useState("#ffffff");
  const [bgPreviewUrl, setBgPreviewUrl]   = useState(null);
  const [localSource, setLocalSource]     = useState(null);
  const [bgError, setBgError]             = useState(null);

  // Depth bokeh
  const [bokehPhase, setBokehPhase]       = useState("idle"); // idle | loading | processing | ready
  const [bokehProgress, setBokehProgress] = useState(0);
  const [bokehDepth, setBokehDepth]       = useState(null);   // { depthCanvas, origW, origH }
  const [bokehBlur, setBokehBlur]         = useState(16);
  const [bokehError, setBokehError]       = useState(null);

  const effectiveSource = useMemo(() => localSource || sourceBlob, [localSource, sourceBlob]);

  useEffect(() => {
    if (!effectiveSource) return;
    const u = URL.createObjectURL(effectiveSource);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [effectiveSource]);

  // Regenerate BG preview whenever fg canvas or background choice changes
  useEffect(() => {
    if (!bgFg) {
      setBgPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      return;
    }
    let cancelled = false;
    const preset  = BG_PRESETS.find((p) => p.id === bgChoice);
    const bgValue = bgChoice === "custom" ? bgCustomColor : (preset?.value ?? null);
    compositeBg(bgFg, effectiveSource, bgValue)
      .then((blob) => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        setBgPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return u; });
      })
      .catch(console.error);
    return () => { cancelled = true; };
  }, [bgFg, bgChoice, bgCustomColor]);

  // Live preview filter
  const previewStyle = useMemo(() => {
    const base = {
      transform: `rotate(${rotation + straighten}deg) scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`,
    };
    if (showBefore) return base;
    return { ...base, filter: buildFilter(adjustments) };
  }, [adjustments, flipX, flipY, rotation, straighten, showBefore]);

  // Undo/redo
  const snapshot = () => ({ adjustments, hsl, rotation, straighten, flipX, flipY, label, labelSize, labelPos, showDate, frame });

  const commit = (fn) => {
    setHistory((h) => [...h.slice(-19), snapshot()]);
    setFuture([]);
    setDirty(true);
    fn();
  };

  const undo = () => {
    const last = history.at(-1);
    if (!last) return;
    setFuture((f) => [snapshot(), ...f]);
    setHistory((h) => h.slice(0, -1));
    applySnapshot(last);
  };

  const redo = () => {
    const next = future[0];
    if (!next) return;
    setHistory((h) => [...h, snapshot()]);
    setFuture((f) => f.slice(1));
    applySnapshot(next);
  };

  const applySnapshot = (s) => {
    setAdj(s.adjustments);
    setHsl(s.hsl);
    setRotation(s.rotation);
    setStraighten(s.straighten ?? 0);
    setFlipX(s.flipX);
    setFlipY(s.flipY);
    setLabel(s.label);
    setLabelSize(s.labelSize ?? 2);
    setLabelPos(s.labelPos ?? "bottom");
    setShowDate(s.showDate ?? false);
    setFrame(s.frame ?? "none");
  };

  // Slider helpers
  const handleSlider = (key, val) => {
    setAdj((a) => ({ ...a, [key]: val }));
    setDirty(true);
    setPreset(null);
  };
  const resetSlider = (key) => commit(() => setAdj((a) => ({ ...a, [key]: 0 })));

  // Preset apply
  const applyPreset = (preset) => {
    commit(() => {
      setAdj({ ...DEFAULT_ADJUSTMENTS, ...preset.adj });
      setPreset(preset.id);
    });
  };

  // Export
  const makeBlob = () =>
    renderEditedBlob(effectiveSource, {
      adjustments, rotation, straighten, flipX, flipY,
      text: label, textSize: labelSize, textPos: labelPos, showDate, frame,
    });

  const saveEdited = async () => {
    if (!sourceBlob) return;
    setSaving(true);
    try {
      const editedBlob = await makeBlob();
      const updated = photo?.id
        ? { ...photo, editedBlob, updatedAt: new Date().toISOString() }
        : await savePhoto({ blob: effectiveSource, editedBlob });
      if (photo?.id) await putRecord("photos", updated);
      onSaved?.(updated);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const doExport = async () => {
    const blob = await makeBlob();
    if (exportFmt === "pdf") return exportPdfFromImage(blob, "lenspro-photo.pdf");
    exportPhotoBlob(blob, { format: exportFmt, filename: "lenspro-edited" });
  };

  // Background removal handlers
  const handleRemoveBg = async () => {
    setBgPhase("loading");
    setBgProgress(0);
    setBgError(null);
    try {
      await loadBgModel((p) => setBgProgress(p));
      setBgPhase("processing");
      const fg = await removeBg(effectiveSource);
      setBgFg(fg);
      setBgPhase("ready");
    } catch (err) {
      console.error("BG removal failed:", err);
      setBgError(err?.message || "Unknown error");
      setBgPhase("idle");
    }
  };

  const applyBg = async () => {
    if (!bgFg) return;
    const preset  = BG_PRESETS.find((p) => p.id === bgChoice);
    const bgValue = bgChoice === "custom" ? bgCustomColor : (preset?.value ?? null);
    const blob    = await compositeBg(bgFg, effectiveSource, bgValue);
    setBgFg(null);
    setBgPhase("idle");
    setBgPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setLocalSource(blob);
    setDirty(true);
  };

  const resetBg = () => {
    setBgFg(null);
    setBgPhase("idle");
    setBgPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setLocalSource(null);
  };

  const handleDepthBokeh = async () => {
    setBokehPhase("loading");
    setBokehProgress(0);
    setBokehError(null);
    try {
      await loadDepthModel((p) => setBokehProgress(p));
      setBokehPhase("processing");
      const result = await getDepthMap(effectiveSource);
      setBokehDepth(result);
      setBokehPhase("ready");
    } catch (err) {
      console.error("Depth bokeh failed:", err);
      setBokehError(err?.message || "Unknown error");
      setBokehPhase("idle");
    }
  };

  const applyBokeh = async () => {
    if (!bokehDepth) return;
    try {
      setBokehPhase("processing");
      const blob = await applyDepthBokeh(effectiveSource, bokehDepth.depthCanvas, bokehBlur);
      setBokehDepth(null);
      setBokehPhase("idle");
      setLocalSource(blob);
      setDirty(true);
    } catch (err) {
      console.error("Apply bokeh failed:", err);
      setBokehError(err?.message || "Unknown error");
      setBokehPhase("idle");
    }
  };

  const cancelBokeh = () => {
    setBokehDepth(null);
    setBokehPhase("idle");
    setBokehError(null);
  };

  const getSwatchStyle = (value) => {
    if (value === null) return { background: "repeating-conic-gradient(rgba(255,255,255,.15) 0% 25%, rgba(255,255,255,.04) 0% 50%) 0 0 / 10px 10px" };
    if (value === "blur") return { background: "linear-gradient(135deg,#374151,#6b7280)" };
    return { background: value };
  };

  // Active adjustments count (badge)
  const activeCount = Object.values(adjustments).filter((v) => v !== 0).length;

  const filteredPresets = presetCat === "All"
    ? PRESETS
    : PRESETS.filter((p) => p.cat === presetCat);

  if (!sourceBlob) {
    return (
      <section className="panel-page empty-page">
        <div style={{ textAlign: "center", marginTop: "30vh" }}>
          <Sparkles size={48} style={{ color: "var(--accent)", marginBottom: 16 }} />
          <h1 style={{ margin: "0 0 8px" }}>Studio</h1>
          <p>Capture or select a photo to open the editor.<br />Adjust, color-grade, transform, frame, and export — all offline.</p>
        </div>
      </section>
    );
  }

  // ── Video items: full Video Studio editor ─────────────────────────────────
  if (photo?.type === "video" || photo?.meta?.type === "video") {
    return <VideoEditor photo={photo} onSaved={onSaved} />;
  }

  return (
    <section className={`studio-page${panelOpen ? "" : " panel-collapsed"}`}>

      {/* ── Header ── */}
      <header className="st-header safe-top">
        <div className="st-header-left">
          <button className="icon-button glass" type="button" onClick={undo} disabled={!history.length} title="Undo">
            <Undo2 size={18} />
          </button>
          <button className="icon-button glass" type="button" onClick={redo} disabled={!future.length} title="Redo">
            <Redo2 size={18} />
          </button>
        </div>

        <div className="st-title">
          <strong>Studio</strong>
          {activeCount > 0 && <span className="st-adj-badge">{activeCount}</span>}
          <span className="st-status">{dirty ? "● Unsaved" : "Saved"}</span>
        </div>

        <div className="st-header-right">
          <button
            type="button"
            className={`st-compare-btn ${showBefore ? "is-active" : ""}`}
            onClick={() => setShowBefore((v) => !v)}
            title="Before / After"
          >
            <ArrowLeftRight size={14} />
            {showBefore ? "Before" : "After"}
          </button>
          <button
            className="primary-button small"
            type="button"
            onClick={saveEdited}
            disabled={saving}
          >
            <Save size={15} />{saving ? "…" : "Save"}
          </button>
        </div>
      </header>

      {/* ── Canvas ── */}
      <div className="st-canvas">
        <div className="st-canvas-inner">
          <img
            src={bgPreviewUrl || url}
            alt="Editing preview"
            className="st-photo"
            style={previewStyle}
          />

          {/* Frame overlay */}
          {frame !== "none" && (
            <div className={`st-frame-overlay st-frame-${frame}`} />
          )}

          {/* Text label preview */}
          {label && !showBefore && (
            <div className={`st-label-overlay st-label-${labelPos} st-label-size-${labelSize}`}>
              {label}
            </div>
          )}

          {/* Date stamp preview */}
          {showDate && !showBefore && (
            <div className="st-date-stamp">
              {new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            </div>
          )}

          {/* Before badge */}
          {showBefore && (
            <div className="st-before-badge">ORIGINAL</div>
          )}
        </div>
      </div>

      {/* ── Editor panel ── */}
      <div className={`st-panel${panelOpen ? "" : " is-collapsed"}`}>

        {/* Drag handle — tap to collapse / expand */}
        <button
          type="button"
          className="st-panel-toggle"
          onClick={() => setPanelOpen((v) => !v)}
          aria-label={panelOpen ? "Collapse editor" : "Expand editor"}
        >
          <span className="st-panel-pill" />
        </button>

        {/* Icon tab bar */}
        <div className="st-tabs">
          {TABS.map(({ id, label: lbl, Icon }) => (
            <button
              key={id}
              type="button"
              className={`st-tab ${tab === id ? "is-active" : ""}`}
              onClick={() => setTab(id)}
              title={lbl}
            >
              <Icon size={17} />
              {tab === id && <span>{lbl}</span>}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="st-content">

          {/* ─ Light ─ */}
          {tab === "light" && (
            <SliderGroup
              sliders={LIGHT_SLIDERS}
              adjustments={adjustments}
              onChange={handleSlider}
              onReset={resetSlider}
            />
          )}

          {/* ─ Color ─ */}
          {tab === "color" && (
            <div>
              {/* Mode toggle */}
              <div className="st-color-mode-bar">
                <button
                  type="button"
                  className={`st-mode-pill ${colorMode === "basic" ? "is-active" : ""}`}
                  onClick={() => setColorMode("basic")}
                >Basic</button>
                <button
                  type="button"
                  className={`st-mode-pill ${colorMode === "hsl" ? "is-active" : ""}`}
                  onClick={() => setColorMode("hsl")}
                >HSL Mixer</button>
              </div>

              {colorMode === "basic" && (
                <SliderGroup
                  sliders={COLOR_SLIDERS}
                  adjustments={adjustments}
                  onChange={handleSlider}
                  onReset={resetSlider}
                />
              )}

              {colorMode === "hsl" && (
                <div>
                  {/* Hue pills */}
                  <div className="st-hue-pills">
                    {HSL_HUES.map((h) => (
                      <button
                        key={h.key}
                        type="button"
                        className={`st-hue-pill ${activeHue === h.key ? "is-active" : ""}`}
                        style={{ "--hue-color": h.color }}
                        onClick={() => setActiveHue(h.key)}
                      >
                        <span className="st-hue-dot" style={{ background: h.color }} />
                        {h.label}
                      </button>
                    ))}
                  </div>
                  {/* H/S/L sliders */}
                  <div className="st-slider-group" style={{ marginTop: 12 }}>
                    {[
                      { key: "hue", label: "Hue",        min: -60, max: 60 },
                      { key: "sat", label: "Saturation",  min: -60, max: 60 },
                      { key: "lum", label: "Luminance",   min: -60, max: 60 },
                    ].map(({ key, label: lbl, min, max }) => (
                      <SliderItem
                        key={key}
                        sliderKey={key}
                        label={lbl}
                        min={min} max={max}
                        value={hsl[activeHue][key]}
                        onChange={(v) => { setHsl((prev) => ({ ...prev, [activeHue]: { ...prev[activeHue], [key]: v } })); setDirty(true); }}
                        onReset={() => commit(() => setHsl((prev) => ({ ...prev, [activeHue]: { ...prev[activeHue], [key]: 0 } })))}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─ Detail ─ */}
          {tab === "detail" && (
            <SliderGroup
              sliders={DETAIL_SLIDERS}
              adjustments={adjustments}
              onChange={handleSlider}
              onReset={resetSlider}
            />
          )}

          {/* ─ Effects ─ */}
          {tab === "effects" && (
            <SliderGroup
              sliders={EFFECT_SLIDERS}
              adjustments={adjustments}
              onChange={handleSlider}
              onReset={resetSlider}
            />
          )}

          {/* ─ Transform ─ */}
          {tab === "transform" && (
            <div className="st-transform">
              {/* Rotate + Flip */}
              <div className="st-section-label">Rotate &amp; Flip</div>
              <div className="st-transform-row">
                <button type="button" className="st-tfm-btn" onClick={() => commit(() => setRotation((v) => v - 90))}>
                  <RotateCcw size={20} /><span>Left</span>
                </button>
                <button type="button" className="st-tfm-btn" onClick={() => commit(() => setRotation((v) => v + 90))}>
                  <RotateCw size={20} /><span>Right</span>
                </button>
                <button type="button" className={`st-tfm-btn ${flipX ? "is-active" : ""}`} onClick={() => commit(() => setFlipX((v) => !v))}>
                  <FlipHorizontal2 size={20} /><span>Flip H</span>
                </button>
                <button type="button" className={`st-tfm-btn ${flipY ? "is-active" : ""}`} onClick={() => commit(() => setFlipY((v) => !v))}>
                  <FlipVertical2 size={20} /><span>Flip V</span>
                </button>
              </div>

              {/* Straighten */}
              <div className="st-section-label" style={{ marginTop: 14 }}>Straighten</div>
              <SliderItem
                sliderKey="straighten"
                label="Angle"
                min={-10} max={10}
                value={straighten}
                onChange={(v) => { setStraighten(v); setDirty(true); }}
                onReset={() => { setStraighten(0); setDirty(true); }}
              />

              {/* Crop ratio */}
              <div className="st-section-label" style={{ marginTop: 14 }}>Crop Ratio</div>
              <div className="st-crop-ratios">
                {CROP_RATIOS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={`st-crop-btn ${cropRatio === r.id ? "is-active" : ""}`}
                    onClick={() => setCropRatio(r.id)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {/* Frame */}
              <div className="st-section-label" style={{ marginTop: 14 }}>Frame / Border</div>
              <div className="st-frame-picker">
                {FRAMES.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`st-frame-btn ${frame === f.id ? "is-active" : ""}`}
                    onClick={() => commit(() => setFrame(f.id))}
                    title={f.label}
                  >
                    <span
                      className="st-frame-swatch"
                      style={{
                        background: f.id === "none" ? "transparent" : f.color,
                        border: f.id === "none" ? "2px dashed rgba(255,255,255,.3)" : "none",
                      }}
                    />
                    <span>{f.label}</span>
                  </button>
                ))}
              </div>

              {/* Reset all */}
              <button
                type="button"
                className="st-reset-all danger-btn"
                onClick={() => commit(() => {
                  setAdj(DEFAULT_ADJUSTMENTS);
                  setHsl(DEFAULT_HSL);
                  setRotation(0);
                  setStraighten(0);
                  setFlipX(false);
                  setFlipY(false);
                  setFrame("none");
                  setCropRatio("free");
                  setPreset(null);
                })}
              >
                <RotateCcw size={15} /> Reset All Edits
              </button>
            </div>
          )}

          {/* ─ Overlay ─ */}
          {tab === "overlay" && (
            <div className="st-overlay-panel">

              {/* ── Background Removal ── */}
              <div className="st-section-label">Background Removal</div>

              {bgPhase === "idle" && (
                <div className="st-bg-idle">
                  <button type="button" className="st-bg-remove-btn" onClick={handleRemoveBg}>
                    <Scissors size={15} /> Remove Background
                  </button>
                  {bgError && (
                    <div className="st-bg-error">{bgError}</div>
                  )}
                  {localSource && (
                    <div className="st-bg-applied">
                      <span>Background replaced</span>
                      <button type="button" className="st-bg-undo-btn" onClick={resetBg}>
                        <RotateCcw size={12} /> Undo
                      </button>
                    </div>
                  )}
                </div>
              )}

              {bgPhase === "loading" && (
                <div className="st-bg-status">
                  <div className="st-bg-progress-track">
                    <div className="st-bg-progress-fill" style={{ width: `${bgProgress}%` }} />
                  </div>
                  <span className="st-bg-status-txt">
                    {bgProgress < 5 ? "Loading AI model…" : `Downloading model ${bgProgress}%`}
                  </span>
                </div>
              )}

              {bgPhase === "processing" && (
                <div className="st-bg-status">
                  <div className="st-bg-spinner" />
                  <span className="st-bg-status-txt">Removing background…</span>
                </div>
              )}

              {bgPhase === "ready" && (
                <div>
                  <div className="st-section-label" style={{ marginTop: 4, marginBottom: 6 }}>Choose Background</div>
                  <div className="st-bg-presets">
                    {BG_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`st-bg-swatch-btn ${bgChoice === p.id ? "is-active" : ""}`}
                        onClick={() => setBgChoice(p.id)}
                        title={p.label}
                      >
                        <span className="st-bg-swatch" style={getSwatchStyle(p.value)} />
                        <span className="st-bg-swatch-label">{p.label}</span>
                      </button>
                    ))}
                    <label
                      className={`st-bg-swatch-btn ${bgChoice === "custom" ? "is-active" : ""}`}
                      title="Custom colour"
                    >
                      <span className="st-bg-swatch" style={{ background: bgCustomColor }} />
                      <span className="st-bg-swatch-label">Custom</span>
                      <input
                        type="color"
                        value={bgCustomColor}
                        className="st-bg-color-input"
                        onChange={(e) => { setBgCustomColor(e.target.value); setBgChoice("custom"); }}
                      />
                    </label>
                  </div>
                  <div className="st-bg-actions">
                    <button type="button" className="primary-button small" onClick={applyBg}>
                      <Check size={14} /> Apply
                    </button>
                    <button
                      type="button"
                      className="st-bg-cancel-btn"
                      onClick={() => {
                        setBgFg(null);
                        setBgPhase("idle");
                        setBgPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Depth Bokeh ── */}
              <div className="st-section-label" style={{ marginTop: 4 }}>Portrait Bokeh</div>

              {bokehPhase === "idle" && (
                <div className="st-bg-idle">
                  <button type="button" className="st-bg-remove-btn" onClick={handleDepthBokeh}>
                    <Aperture size={15} /> Portrait Mode
                  </button>
                  {bokehError && <div className="st-bg-error">{bokehError}</div>}
                </div>
              )}

              {bokehPhase === "loading" && (
                <div className="st-bg-status">
                  <div className="st-bg-progress-track">
                    <div className="st-bg-progress-fill" style={{ width: `${bokehProgress}%` }} />
                  </div>
                  <span className="st-bg-status-txt">
                    {bokehProgress < 5 ? "Loading depth model…" : `Downloading model ${bokehProgress}%`}
                  </span>
                </div>
              )}

              {bokehPhase === "processing" && (
                <div className="st-bg-status">
                  <div className="st-bg-spinner" />
                  <span className="st-bg-status-txt">Analysing depth…</span>
                </div>
              )}

              {bokehPhase === "ready" && (
                <div>
                  <div className="st-section-label" style={{ marginTop: 4, marginBottom: 6 }}>Blur Strength</div>
                  <SliderItem
                    sliderKey="bokehBlur"
                    label="Background Blur"
                    min={4}
                    max={32}
                    value={bokehBlur}
                    onChange={(v) => setBokehBlur(v)}
                    onReset={() => setBokehBlur(16)}
                  />
                  <div className="st-bg-actions" style={{ marginTop: 10 }}>
                    <button type="button" className="primary-button small" onClick={applyBokeh}>
                      <Check size={14} /> Apply
                    </button>
                    <button type="button" className="st-bg-cancel-btn" onClick={cancelBokeh}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Text Label ── */}
              <div className="st-section-label" style={{ marginTop: 8 }}>Text Label</div>
              <textarea
                className="st-text-input"
                value={label}
                onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
                placeholder="Add text to your photo…"
                maxLength={120}
                rows={3}
              />

              {label && (
                <>
                  <div className="st-section-label" style={{ marginTop: 12 }}>Size</div>
                  <div className="st-size-row">
                    {["S", "M", "L"].map((s, i) => (
                      <button
                        key={s}
                        type="button"
                        className={`st-size-btn ${labelSize === i + 1 ? "is-active" : ""}`}
                        onClick={() => setLabelSize(i + 1)}
                      >{s}</button>
                    ))}
                  </div>

                  <div className="st-section-label" style={{ marginTop: 12 }}>Position</div>
                  <div className="st-pos-row">
                    {["top", "center", "bottom"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`st-pos-btn ${labelPos === p ? "is-active" : ""}`}
                        onClick={() => setLabelPos(p)}
                      >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
                    ))}
                  </div>
                </>
              )}

              <div className="st-section-label" style={{ marginTop: 16 }}>Stamps</div>
              <div className="st-stamp-row">
                <button
                  type="button"
                  className={`st-stamp-btn ${showDate ? "is-active" : ""}`}
                  onClick={() => { setShowDate((v) => !v); setDirty(true); }}
                >
                  <Calendar size={15} />
                  Date Stamp
                  {showDate && <Check size={13} />}
                </button>
              </div>
            </div>
          )}

          {/* ─ Presets ─ */}
          {tab === "presets" && (
            <div>
              {/* Category filter */}
              <div className="st-preset-cats">
                {PRESET_CATS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`st-preset-cat ${presetCat === c ? "is-active" : ""}`}
                    onClick={() => setPresetCat(c)}
                  >{c}</button>
                ))}
              </div>
              {/* Preset grid */}
              <div className="st-preset-grid">
                {filteredPresets.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    url={url}
                    isActive={activePreset === preset.id}
                    onClick={() => applyPreset(preset)}
                  />
                ))}
              </div>
              {activePreset && (
                <p className="st-preset-note">
                  <Sparkles size={12} />
                  {PRESETS.find((p) => p.id === activePreset)?.label} — fine-tune in other tabs.
                </p>
              )}
            </div>
          )}

        </div>{/* /st-content */}

        {/* ── Export bar ── */}
        <div className="st-export-bar">
          <div className="st-fmt-pills">
            {["jpg", "png", "pdf"].map((f) => (
              <button
                key={f}
                type="button"
                className={`st-fmt-pill ${exportFmt === f ? "is-active" : ""}`}
                onClick={() => setExportFmt(f)}
              >{f.toUpperCase()}</button>
            ))}
          </div>
          <button type="button" className="st-export-btn" onClick={doExport}>
            <Download size={15} /> Export
          </button>
          <button
            type="button"
            className="st-export-btn ghost"
            onClick={async () => shareBlob(await makeBlob(), "LensPro photo")}
          >
            Share
          </button>
        </div>

      </div>{/* /st-panel */}
    </section>
  );
}
